import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ViewEncapsulation,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { CANVAS_COLOR_VARS, getCanvasColor } from '../../canvas-theme.utils';
import { CanvasToolbarIconComponent } from '../../icons/canvas-toolbar-icon.component';
import type { CanvasLayerPanelEntry } from '../canvas-maps.models';
import { CanvasMarkerPickerComponent } from './canvas-marker-picker.component';

export interface LayerNameChangePayload {
  id: string;
  name: string;
}

@Component({
  selector: 'lib-canvas-layers-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CanvasToolbarIconComponent,
    CanvasMarkerPickerComponent,
  ],
  templateUrl: './canvas-layers-panel.component.html',
  styleUrl: './canvas-layers-panel.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class CanvasLayersPanelComponent implements AfterViewChecked, OnDestroy {
  private _prevLayers: readonly CanvasLayerPanelEntry[] = [];
  private static readonly FLIP_MS = 220;
  private static readonly FLIP_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

  /** When false the panel omits its own header row (used by the morphing widget). */
  @Input() showHeader = true;
  /** Base URL for canvas marker SVGs (`/assets/canvas-icons`). */
  @Input() iconsBaseUrl = '/assets/canvas-icons';

  @HostBinding('class.cv-lp--embedded') get isEmbedded() { return !this.showHeader; }

  @Input({ required: true })
  set layers(value: readonly CanvasLayerPanelEntry[]) {
    // When a brand-new editing row appears, schedule focus for it.
    const prevIds = new Set(this._prevLayers.map((l) => l.id));
    const newEditing = value.find((l) => l.rowState === 'editing' && !prevIds.has(l.id));
    if (newEditing) {
      this.focusPendingId = newEditing.id;
    }
    const entering = value.filter((l) => !prevIds.has(l.id)).map((l) => l.id);
    if (entering.length) {
      this.enterLayerIds.update((s) => new Set([...s, ...entering]));
    }
    this._prevLayers = value;
    this._layers = value;
    if (!this.draggingId) {
      this.previewOrderIds.set(null);
    }
  }
  get layers(): readonly CanvasLayerPanelEntry[] {
    return this._layers;
  }
  private _layers: readonly CanvasLayerPanelEntry[] = [];
  @Output() closePanel = new EventEmitter<void>();
  @Output() toggleVisibility = new EventEmitter<string>();
  @Output() nameChange = new EventEmitter<LayerNameChangePayload>();
  @Output() saveName = new EventEmitter<string>();
  /** Revert name to default and lock field (not the same as deleting the feature). */
  @Output() resetToDefault = new EventEmitter<string>();
  @Output() deleteLayer = new EventEmitter<string>();
  @Output() exportLayer = new EventEmitter<string>();
  @Output() zoomToBounds = new EventEmitter<string>();
  @Output() aboutLayer = new EventEmitter<string>();
  @Output() startEditName = new EventEmitter<string>();
  @Output() layerColorChange = new EventEmitter<{ id: string; color: string }>();
  @Output() layerMarkerChange = new EventEmitter<{ id: string; iconId: number }>();
  @Output() layerOpacityChange = new EventEmitter<{ id: string; opacity: number }>();
  /** Ordered layer ids after drag-and-drop reorder (ui-canvas LayerCard parity). */
  @Output() reorderLayers = new EventEmitter<string[]>();
  /** Remove every layer from the map and panel (ui-canvas clear-all parity). */
  @Output() clearAllLayers = new EventEmitter<void>();
  /** Emitted when the user clicks a layer row (not its controls). */
  @Output() layerSelect = new EventEmitter<string>();

  /** Live order while dragging (null when idle). */
  readonly previewOrderIds = signal<string[] | null>(null);
  readonly draggingLayerId = signal<string | null>(null);
  /** Rows that should play the one-time enter animation. */
  readonly enterLayerIds = signal<Set<string>>(new Set());

  private draggingId: string | null = null;
  private dragPointerId: number | null = null;
  private dragStartClientY = 0;
  private dragRowEl: HTMLElement | null = null;

  readonly openKebabId = signal<string | null>(null);
  /** Viewport coordinates for the fixed kebab menu (not clipped by panel overflow). */
  readonly kebabMenuTop = signal(0);
  readonly kebabMenuLeft = signal(0);
  /** ID of the layer whose input should be focused on the next view-check cycle. */
  private focusPendingId: string | null = null;

  private static readonly KEBAB_MENU_WIDTH = 168;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnDestroy(): void {
    this.cancelDrag();
  }

  /** Layers in panel order (preview while dragging, otherwise input order). */
  displayLayers(): readonly CanvasLayerPanelEntry[] {
    const preview = this.previewOrderIds();
    if (!preview) return this._layers;
    const byId = new Map(this._layers.map((l) => [l.id, l]));
    return preview.map((id) => byId.get(id)).filter((l): l is CanvasLayerPanelEntry => !!l);
  }

  ngAfterViewChecked(): void {
    if (!this.focusPendingId) return;
    const input = this.host.nativeElement.querySelector<HTMLInputElement>(
      `[data-layer-id="${this.focusPendingId}"]`
    );
    if (input) {
      input.focus();
      input.select();
      this.focusPendingId = null;
    }
  }

  shapeColor(layer: CanvasLayerPanelEntry): string {
    if (layer.styleControls.color) {
      return layer.layerColor;
    }
    if (layer.shape === 'dataset') return getCanvasColor(CANVAS_COLOR_VARS.shapeDataset);
    if (layer.shape === 'circle') return getCanvasColor(CANVAS_COLOR_VARS.shapeCircle);
    if (layer.shape === 'rectangle') return getCanvasColor(CANVAS_COLOR_VARS.shapeRectangle);
    if (layer.shape === 'polygon') return getCanvasColor(CANVAS_COLOR_VARS.shapePolygon);
    return getCanvasColor(CANVAS_COLOR_VARS.shapeDefault);
  }

  shapeBgColor(layer: CanvasLayerPanelEntry): string {
    const hex = this.shapeColor(layer).replace('#', '');
    if (hex.length === 6) {
      return `#${hex}22`;
    }
    if (layer.shape === 'dataset') return getCanvasColor(CANVAS_COLOR_VARS.shapeDatasetBg);
    if (layer.shape === 'circle') return getCanvasColor(CANVAS_COLOR_VARS.shapeCircleBg);
    if (layer.shape === 'rectangle') return getCanvasColor(CANVAS_COLOR_VARS.shapeRectangleBg);
    if (layer.shape === 'polygon') return getCanvasColor(CANVAS_COLOR_VARS.shapePolygonBg);
    return getCanvasColor(CANVAS_COLOR_VARS.shapeDefaultBg);
  }

  onColorInput(layerId: string, ev: Event): void {
    const color = (ev.target as HTMLInputElement).value;
    this.layerColorChange.emit({ id: layerId, color });
  }

  onOpacityInput(layerId: string, ev: Event): void {
    const pct = Number((ev.target as HTMLInputElement).value);
    this.layerOpacityChange.emit({ id: layerId, opacity: pct / 100 });
  }

  showStyleControls(layer: CanvasLayerPanelEntry): boolean {
    return (
      layer.rowState !== 'editing' &&
      (layer.styleControls.color ||
        layer.styleControls.marker ||
        layer.styleControls.opacity)
    );
  }

  canDragRow(layer: CanvasLayerPanelEntry): boolean {
    return layer.rowState === 'saved' || layer.rowState === 'reset';
  }

  isEnteringRow(layerId: string): boolean {
    return this.enterLayerIds().has(layerId);
  }

  onRowPointerDown(layerId: string, event: PointerEvent): void {
    if (event.button !== 0) return;
    const layer = this._layers.find((l) => l.id === layerId);
    if (!layer || !this.canDragRow(layer)) return;

    const target = event.target as HTMLElement;
    if (
      target.closest(
        '.cv-lp__controls, .cv-lp__input, .cv-lp__label-btn, .cv-lp__color-input, .cv-lp__color-swatch, lib-canvas-marker-picker'
      )
    ) {
      return;
    }

    const row = target.closest<HTMLElement>('[data-layer-row-id]');
    if (!row) return;

    event.preventDefault();
    this.closeKebab();
    this.draggingId = layerId;
    this.dragPointerId = event.pointerId;
    this.dragStartClientY = event.clientY;
    this.dragRowEl = row;
    this.previewOrderIds.set(this._layers.map((l) => l.id));
    this.draggingLayerId.set(layerId);
    row.setPointerCapture(event.pointerId);
    row.classList.add('cv-lp__row--dragging');
  }

  @HostListener('document:pointermove', ['$event'])
  onDocumentPointerMove(event: PointerEvent): void {
    if (!this.draggingId || event.pointerId !== this.dragPointerId) return;

    const order = this.previewOrderIds();
    if (!order || !this.dragRowEl) return;

    const deltaY = event.clientY - this.dragStartClientY;
    this.dragRowEl.style.transform = `translateY(${deltaY}px) scale(1.02)`;

    const from = order.indexOf(this.draggingId);
    if (from < 0) return;

    const targetIndex = this.getDropIndexAt(event.clientY);
    if (targetIndex < 0 || from === targetIndex) return;

    const stride = this.measureRowStride();
    const before = this.captureRowRects();
    const next = [...order];
    next.splice(from, 1);
    next.splice(targetIndex, 0, this.draggingId);
    this.dragStartClientY += (targetIndex - from) * stride;
    this.previewOrderIds.set(next);
    this.cdr.detectChanges();

    requestAnimationFrame(() => {
      this.runFlip(before, this.draggingId);
    });
  }

  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  onDocumentPointerUp(event: PointerEvent): void {
    if (!this.draggingId || event.pointerId !== this.dragPointerId) return;
    this.finishDrag();
  }

  private finishDrag(): void {
    const order = this.previewOrderIds();
    const committed = order ?? this._layers.map((l) => l.id);
    const current = this._layers.map((l) => l.id);
    const changed =
      committed.length !== current.length ||
      committed.some((id, i) => id !== current[i]);
    const rowEl = this.dragRowEl;
    const pointerId = this.dragPointerId;

    const finalize = (): void => {
      if (rowEl) {
        if (pointerId !== null) {
          try {
            rowEl.releasePointerCapture(pointerId);
          } catch {
            /* row may already be gone */
          }
        }
        rowEl.classList.remove('cv-lp__row--dragging');
        rowEl.style.transform = '';
        rowEl.style.transition = '';
        rowEl.style.zIndex = '';
      }
      if (changed) {
        this.reorderLayers.emit(committed);
      }
      this.previewOrderIds.set(null);
      this.draggingLayerId.set(null);
      this.draggingId = null;
      this.dragPointerId = null;
      this.dragRowEl = null;
    };

    if (rowEl) {
      rowEl.classList.remove('cv-lp__row--dragging');
      rowEl.style.transition = `transform ${CanvasLayersPanelComponent.FLIP_MS}ms ${CanvasLayersPanelComponent.FLIP_EASE}`;
      rowEl.style.transform = '';

      let settled = false;
      const settle = (): void => {
        if (settled) return;
        settled = true;
        finalize();
      };
      rowEl.addEventListener('transitionend', settle, { once: true });
      window.setTimeout(settle, CanvasLayersPanelComponent.FLIP_MS + 40);
      return;
    }

    finalize();
  }

  private cancelDrag(): void {
    if (!this.draggingId) return;
    const rowEl = this.dragRowEl;
    const pointerId = this.dragPointerId;
    if (rowEl) {
      if (pointerId !== null) {
        try {
          rowEl.releasePointerCapture(pointerId);
        } catch {
          /* ignore */
        }
      }
      rowEl.classList.remove('cv-lp__row--dragging');
      rowEl.style.transform = '';
      rowEl.style.transition = '';
      rowEl.style.zIndex = '';
    }
    this.previewOrderIds.set(null);
    this.draggingLayerId.set(null);
    this.draggingId = null;
    this.dragPointerId = null;
    this.dragRowEl = null;
  }

  private getDropIndexAt(clientY: number): number {
    const rows = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('[data-layer-row-id]')
    );
    if (!rows.length) return 0;

    for (let i = 0; i < rows.length; i++) {
      const rect = rows[i].getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      if (clientY < mid) return i;
    }
    return rows.length - 1;
  }

  private measureRowStride(): number {
    const rows = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('[data-layer-row-id]')
    );
    if (rows.length < 2) {
      return rows[0]?.getBoundingClientRect().height ?? 40;
    }
    const first = rows[0].getBoundingClientRect();
    const second = rows[1].getBoundingClientRect();
    return Math.max(28, second.top - first.top);
  }

  private captureRowRects(): Map<string, DOMRect> {
    const map = new Map<string, DOMRect>();
    this.host.nativeElement
      .querySelectorAll<HTMLElement>('[data-layer-row-id]')
      .forEach((el) => {
        const id = el.dataset['layerRowId'];
        if (id) map.set(id, el.getBoundingClientRect());
      });
    return map;
  }

  private runFlip(before: Map<string, DOMRect>, skipId: string | null): void {
    const after = this.captureRowRects();
    before.forEach((rect, id) => {
      if (id === skipId) return;
      const el = this.host.nativeElement.querySelector<HTMLElement>(
        `[data-layer-row-id="${id}"]`
      );
      if (!el) return;
      const next = after.get(id);
      if (!next) return;
      const dx = rect.left - next.left;
      const dy = rect.top - next.top;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;

      el.style.transition = 'none';
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      requestAnimationFrame(() => {
        el.style.transition = `transform ${CanvasLayersPanelComponent.FLIP_MS}ms ${CanvasLayersPanelComponent.FLIP_EASE}`;
        el.style.transform = '';
        const onEnd = () => {
          el.style.transition = '';
          el.removeEventListener('transitionend', onEnd);
        };
        el.addEventListener('transitionend', onEnd);
      });
    });
  }

  trackById(_i: number, e: CanvasLayerPanelEntry): string {
    return e.id;
  }

  onNameInput(layer: CanvasLayerPanelEntry, value: string): void {
    if (layer.rowState !== 'editing') return;
    this.nameChange.emit({ id: layer.id, name: value });
  }

  toggleKebab(id: string, ev: MouseEvent): void {
    ev.stopPropagation();
    const current = this.openKebabId();
    if (current === id) {
      this.closeKebab();
      return;
    }
    const btn = ev.currentTarget as HTMLElement;
    const r = btn.getBoundingClientRect();
    const w = CanvasLayersPanelComponent.KEBAB_MENU_WIDTH;
    const left = Math.min(window.innerWidth - w - 8, Math.max(8, r.right - w));
    const top = r.bottom + 4;
    this.kebabMenuTop.set(top);
    this.kebabMenuLeft.set(left);
    this.openKebabId.set(id);
  }

  closeKebab(): void {
    this.openKebabId.set(null);
  }

  onLabelClick(layer: CanvasLayerPanelEntry): void {
    this.focusPendingId = layer.id;
    this.startEditName.emit(layer.id);
  }

  onRowClick(layer: CanvasLayerPanelEntry, ev: MouseEvent): void {
    const target = ev.target as HTMLElement;
    if (
      target.closest(
        '.cv-lp__controls, .cv-lp__color-picker, .cv-lp__color-input, .cv-lp__color-swatch, lib-canvas-marker-picker, .cv-lp__input',
      )
    ) {
      return;
    }
    this.layerSelect.emit(layer.id);
  }

  onMenuEdit(id: string): void {
    this.focusPendingId = id;
    this.startEditName.emit(id);
    this.closeKebab();
  }

  onMenuDelete(id: string): void {
    this.deleteLayer.emit(id);
    this.closeKebab();
  }

  onMenuExport(id: string): void {
    this.exportLayer.emit(id);
    this.closeKebab();
  }

  onMenuZoomToBounds(id: string): void {
    this.zoomToBounds.emit(id);
    this.closeKebab();
  }

  onMenuAbout(id: string): void {
    this.aboutLayer.emit(id);
    this.closeKebab();
  }

  showAboutMenuItem(layer: CanvasLayerPanelEntry): boolean {
    return layer.kind === 'dataset' && !!layer.datasetId;
  }

  /** Whether the open kebab menu should show the About action. */
  aboutMenuVisible(): boolean {
    const id = this.openKebabId();
    if (!id) return false;
    const layer = this._layers.find((l) => l.id === id);
    return layer ? this.showAboutMenuItem(layer) : false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent): void {
    if (!this.openKebabId()) return;
    const t = ev.target as Node;
    if (!this.host.nativeElement.contains(t)) {
      this.closeKebab();
    }
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onViewportChange(): void {
    if (this.openKebabId()) {
      this.closeKebab();
    }
  }
}
